/**
 * Module 00 — Simple Mode · single-page "connect & go" surface (0.2)
 *
 * Master-switch surface (no sidebar). Workspace selector re-keys every
 * credential/snippet string to `ws_<slug>` (SM-04); credentials card supports
 * Reveal/Hide + Rotate key (SM-06); copy buttons copy full snippets (SM-05);
 * MCP client picker swaps the intro/steps/right-pane (SM-07). The top-bar mode
 * switch returns to Advanced (SM-02).
 */

'use client';

import * as React from 'react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

import { Icon } from '../icons';
import { WexaLogo } from '../WexaLogo';
import { ModeSwitch } from '../ModeSwitch';
import { CodeBlock, CopyBtn } from './CodeBlock';
import {
  WORKSPACES,
  FLOW,
  MCP_CLIENTS,
  MCP_CONTENT,
  DEFAULT_CONNECTION,
  shortToken,
  type Workspace,
  type McpClientId,
  type ConnectionInfo,
} from './constants';
import type { Theme, Mode } from '@/lib/shell/persistence';
import { type RippleOrigin, rippleOrigin } from '@/lib/shell/themeRipple';

type ConnectTab = 'mcp' | 'api' | 'sdk';

export interface SimpleModeProps {
  theme: Theme;
  toggleTheme: (origin?: RippleOrigin) => void;
  mode: Mode;
  setMode: (m: Mode) => void;
  userInitials?: string;
  workspaces?: Workspace[];
  /** MCP/API/SDK coordinates from the api-gateway; falls back to defaults. */
  connection?: ConnectionInfo;
  /** Mints a real `fab_sk_…` key for the selected workspace (shown once). */
  onGenerateApiKey?: (workspace: Workspace) => Promise<string>;
  /** Simple-only role: hide every exit to the advanced console (the mode switch
   *  and the "Open advanced console" button). The user can never leave Simple. */
  locked?: boolean;
}

const KEY_PLACEHOLDER = '<YOUR_API_KEY>';

export function SimpleMode({
  theme,
  toggleTheme,
  mode,
  setMode,
  locked = false,
  userInitials = '—',
  workspaces = WORKSPACES,
  connection = DEFAULT_CONNECTION,
  onGenerateApiKey,
}: SimpleModeProps) {
  const wsList = workspaces.length ? workspaces : WORKSPACES;
  const [ws, setWs] = React.useState<Workspace>(wsList[0]);
  const [wsOpen, setWsOpen] = React.useState(false);
  const [tab, setTab] = React.useState<ConnectTab>('mcp');
  const [mcpClient, setMcpClient] = React.useState<McpClientId>('claude_ai');
  // Real key issuance: the secret is returned once at creation and never again.
  const [apiKey, setApiKey] = React.useState<string | null>(null);
  const [generating, setGenerating] = React.useState(false);

  // When the workspace set changes (e.g. real projects arrive), keep the
  // selection valid and drop any key shown for the previous workspace.
  React.useEffect(() => {
    setWs((cur) => wsList.find((w) => w.slug === cur.slug) ?? wsList[0]);
    setApiKey(null);
  }, [wsList]);

  const wsId = `ws_${ws.slug}`;
  // ws.slug is the real project id (workspacesFromProjects sets slug: p.id) —
  // a project-scoped MCP URL locks the OAuth consent screen to this workspace
  // and stops every project from showing the identical connect URL.
  const projectMcpUrl = connection.mcpServerUrl.includes('{projectId}')
    ? connection.mcpServerUrl.replace('{projectId}', ws.slug)
    : `${connection.mcpServerUrl.replace(/\/$/, '')}/${ws.slug}`;
  // Full secret goes into copyable snippets so they work as-is; a short form is
  // used for inline display. Both fall back to a placeholder before generation.
  const keyForSnippet = apiKey ?? KEY_PLACEHOLDER;
  const keyShort = apiKey ? shortToken(apiKey) : KEY_PLACEHOLDER;
  const mcpC = MCP_CONTENT[mcpClient];

  const selectWorkspace = (w: Workspace) => {
    setWs(w);
    setApiKey(null);
    setWsOpen(false);
  };

  const generateKey = async () => {
    if (!onGenerateApiKey) {
      toast.error('API key generation isn’t available here.');
      return;
    }
    setGenerating(true);
    try {
      const secret = await onGenerateApiKey(ws);
      setApiKey(secret);
      toast.success('API key generated — copy it now, it won’t be shown again.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not generate API key.');
    } finally {
      setGenerating(false);
    }
  };

  const mcpCode = `{
  "mcpServers": {
    "wexa-context": {
      "url": "${projectMcpUrl}",
      "headers": {
        "Authorization": "Bearer ${keyForSnippet}"
      }
    }
  }
}`;
  const CYPHER =
    'MATCH (c:Customer) WHERE c.project_id = $project_id RETURN c LIMIT 10';
  const apiCode = `curl ${connection.apiBaseUrl}/query-context \\
  -H "Authorization: Bearer ${keyForSnippet}" \\
  -H "Content-Type: application/json" \\
  -d '{"query": "${CYPHER}"}'`;
  const pyCode = `# ${connection.sdk.python.install}
from wexa import Fabric

fabric = Fabric(
    workspace="${connection.issuer}",
    api_key="${keyForSnippet}",
)

result = fabric.query_context(query="${CYPHER}")
for row in result["rows"]:
    print(row)`;

  const steps: Record<ConnectTab, string[]> = {
    mcp: [
      'Paste this block into your MCP client config (e.g. claude_desktop_config.json).',
      'Drop in your API key and restart the client.',
      'Your agent now has live, auto-organized context — nothing to sync.',
    ],
    api: [
      'Send authenticated requests to the Context API endpoint.',
      'Pass read-only Cypher that filters on $project_id — your project is bound to the key.',
      'Wexa returns columns, rows and a row count.',
    ],
    sdk: [
      `${connection.sdk.python.install} — one module, standard library only.`,
      'Initialize Fabric with your workspace URL and API key.',
      'Call query_context() from anywhere in your agent.',
    ],
  };

  return (
    <div data-testid="simple-mode" className="flex h-screen flex-col bg-cs-surface-page text-cs-text-primary">
      {/* header */}
      <header className="flex h-14 flex-shrink-0 items-center gap-2 border-b border-cs-border px-5">
        {/* FAB-362: the real Wexa mark, styled exactly as the advanced console's
            sidebar shows it — the mark on its own in the text colour, not boxed
            in a purple tile. A generic sparkle here left the product unbranded. */}
        <span className="flex shrink-0 items-center text-cs-text-primary">
          <WexaLogo size={24} />
        </span>
        <span className="text-[15.5px] font-bold tracking-tight">Wexa</span>
        <span className="ml-1 rounded-full bg-cs-primary-500/15 px-2 py-0.5 text-[11px] font-medium text-cs-primary-400">
          Simple
        </span>
        <div className="ml-auto flex items-center gap-2">
          {!locked && <ModeSwitch mode={mode} setMode={setMode} />}
          {!locked && <div className="mx-1 h-5 w-px bg-cs-border" />}
          <button
            type="button"
            data-testid="theme-toggle"
            aria-label="Toggle theme"
            onClick={(e) => toggleTheme(rippleOrigin(e.currentTarget))}
            className="flex h-8 w-8 items-center justify-center rounded-md text-cs-text-secondary hover:bg-cs-surface-hover hover:text-cs-text-primary"
          >
            <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={18} />
          </button>
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-cs-surface-raised text-xs font-semibold">
            {userInitials}
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-5xl px-6 py-12">
          {/* hero */}
          <div className="mb-10 text-center">
            {/* FAB-362: the sparkle here read as a brand mark on a category
                label. The real logo belongs in the header, not on a pill. */}
            <div className="mb-4 inline-flex items-center rounded-full bg-cs-primary-500/15 px-3 py-1 text-[11.5px] font-bold uppercase tracking-widest text-cs-primary-400">
              Context platform
            </div>
            <h1 className="mb-3.5 text-[40px] font-bold leading-[1.1] tracking-tight">
              Connect once. Wexa organizes the rest.
            </h1>
            <p className="mx-auto max-w-[660px] text-[16.5px] leading-relaxed text-cs-text-secondary">
              Point your data at Wexa and it builds and maintains the ontology for you — entities,
              relationships and facts are discovered and kept current automatically. Any agent can
              query that context over <b className="text-cs-text-primary">MCP, API, or SDK</b>.
            </p>
            <div className="mt-6 flex justify-center gap-2.5">
              <a
                  href="#connect"
                  className="inline-flex items-center gap-2 rounded-md bg-cs-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-cs-primary-600"
                >
                  <Icon name="plug" size={15} /> Get connection details
                </a>
              {!locked && (
                <button
                  type="button"
                  data-testid="open-advanced"
                  onClick={() => setMode('advanced')}
                  className="inline-flex items-center gap-2 rounded-md border border-cs-border px-4 py-2 text-sm font-medium text-cs-text-primary hover:bg-cs-surface-hover"
                >
                  <Icon name="sliders" size={15} /> Open advanced console
                </button>
              )}
            </div>
          </div>

          {/* how it works */}
          <div className="mb-11 grid grid-cols-1 gap-3 md:grid-cols-4">
            {FLOW.map((f, i) => (
              <div key={f.title} className="rounded-xl border border-cs-border bg-cs-surface-card p-4">
                <div
                  className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl"
                  style={{ background: `color-mix(in srgb, ${f.color} 14%, transparent)`, color: f.color }}
                >
                  <Icon name={f.icon} size={20} />
                </div>
                <div className="mb-1 text-sm font-semibold">
                  <span className="mr-1.5 font-mono text-[11px] text-cs-text-muted">0{i + 1}</span>
                  {f.title}
                </div>
                <div className="text-[12.5px] leading-relaxed text-cs-text-secondary">{f.desc}</div>
              </div>
            ))}
          </div>

          {/* workspace + credentials */}
          <div id="connect" className="mb-6 grid grid-cols-1 items-start gap-5 md:grid-cols-[320px_1fr]">
            {/* workspace selector */}
            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-cs-text-muted">
                1 · Select workspace
              </p>
              <div className="relative">
                <button
                  type="button"
                  data-testid="workspace-selector"
                  onClick={() => setWsOpen((o) => !o)}
                  aria-expanded={wsOpen}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-xl border bg-cs-surface-card p-3.5 text-left',
                    wsOpen ? 'border-cs-primary-500' : 'border-cs-border',
                  )}
                >
                  <div
                    className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-white"
                    style={{ background: ws.color }}
                  >
                    <Icon name="org" size={19} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold" data-testid="workspace-name">{ws.name}</div>
                    <div className="text-[11.5px] text-cs-text-muted">
                      {ws.region ? `${ws.region} · ${ws.nodes} nodes` : 'Self-organizing workspace'}
                    </div>
                  </div>
                  <Icon name="chevdown" size={16} className="text-cs-text-muted" />
                </button>
                {wsOpen && (
                  <div
                    role="menu"
                    data-testid="workspace-menu"
                    className="absolute left-0 right-0 top-full z-50 mt-1 rounded-xl border border-cs-border bg-cs-surface-card p-1 shadow-2xl"
                  >
                    {wsList.map((w) => (
                      <button key={w.slug}
                        type="button"
                        role="menuitem"
                        data-testid={`workspace-opt-${w.slug}`}
                        onClick={() => selectWorkspace(w)}
                        className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-cs-surface-hover"
                      >
                        <div
                          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-white"
                          style={{ background: w.color }}
                        >
                          <Icon name="org" size={15} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[13px] font-semibold">{w.name}</div>
                          <div className="text-[11px] text-cs-text-muted">{w.region ?? 'Workspace'}</div>
                        </div>
                        {w.slug === ws.slug && <Icon name="check" size={15} className="text-cs-primary-400" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-3.5 flex gap-2.5 rounded-xl bg-cs-accent-500/10 p-3.5">
                <Icon name="knowledgegraph" size={17} className="mt-0.5 flex-shrink-0 text-cs-accent-400" />
                <div className="text-xs leading-relaxed text-cs-text-secondary">
                  <b className="text-cs-text-primary">Self-organizing.</b> Wexa is already maintaining
                  the ontology for this workspace. New data is mapped and indexed automatically — you
                  never define a schema.
                </div>
              </div>
            </div>

            {/* credentials */}
            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-cs-text-muted">
                2 · MCP server credentials
              </p>
              <div className="rounded-xl border border-cs-border bg-cs-surface-card px-4 py-3">
                <CredRow label="MCP Server URL" value={projectMcpUrl} copy={projectMcpUrl} />
                <CredRow label="API Base" value={connection.apiBaseUrl} copy={connection.apiBaseUrl} />
                <CredRow label="Workspace ID" value={wsId} copy={wsId} testid="cred-workspace" />
                {locked && (
                  <div className="flex items-center gap-3 border-b border-cs-border py-2 last:border-b-0">
                    <span className="w-32 flex-shrink-0 text-xs text-cs-text-muted">API Key</span>
                    {apiKey ? (
                      <>
                        <span
                          data-testid="api-key-value"
                          className="flex-1 truncate font-mono text-[12.5px] text-cs-text-primary"
                        >
                          {apiKey}
                        </span>
                        <CopyBtn text={apiKey} />
                      </>
                    ) : (
                      <>
                        <span data-testid="api-key-value" className="flex-1 truncate text-[12.5px] text-cs-text-muted">
                          No key yet — generate one to connect
                        </span>
                        <button
                          type="button"
                          data-testid="generate-key"
                          onClick={generateKey}
                          disabled={generating}
                          className="inline-flex items-center gap-1 rounded-md border border-cs-border px-2 py-1 text-[11px] text-cs-text-secondary hover:bg-cs-surface-hover disabled:opacity-50"
                        >
                          <Icon name={generating ? 'refresh' : 'plus'} size={13} />
                          {generating ? 'Generating…' : 'Generate API key'}
                        </button>
                      </>
                    )}
                  </div>
                )}
                <div className="mt-3 flex items-center gap-2 border-t border-cs-border pt-3">
                  <Icon name="lock" size={14} className="text-cs-text-muted" />
                  <span className="text-[11.5px] text-cs-text-muted">
                    {!locked
                      ? 'Snippets below use a placeholder key. Generate a project-scoped key (fab_sk_…) from the API keys page in the advanced console.'
                      : apiKey
                        ? 'Shown once — store it now. The secret is not retrievable again.'
                        : 'Generates a project-scoped key (fab_sk_…). Read access to this workspace’s context.'}
                  </span>
                  {locked && apiKey && (
                    <button
                      type="button"
                      data-testid="regenerate-key"
                      onClick={generateKey}
                      disabled={generating}
                      className="ml-auto inline-flex items-center gap-1 rounded-md border border-cs-border px-2 py-1 text-[11px] text-cs-text-secondary hover:bg-cs-surface-hover disabled:opacity-50"
                    >
                      <Icon name="refresh" size={13} /> Regenerate
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* connect tabs */}
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-cs-text-muted">
            3 · Connect your agent
          </p>
          <div className="overflow-hidden rounded-xl border border-cs-border bg-cs-surface-card">
            <div className="flex gap-1 border-b border-cs-border bg-cs-surface-raised px-4">
              {(
                [
                  ['mcp', 'MCP', 'mcp'],
                  ['api', 'API', 'terminal'],
                  ['sdk', 'SDK', 'models'],
                ] as [ConnectTab, string, string][]
              ).map(([k, l, ic]) => (
                <button key={k}
                  type="button"
                  data-testid={`tab-${k}`}
                  onClick={() => setTab(k)}
                  className={cn(
                    'flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors',
                    tab === k
                      ? 'border-cs-primary-500 text-cs-text-primary'
                      : 'border-transparent text-cs-text-muted hover:text-cs-text-secondary',
                  )}
                >
                  <Icon name={ic} size={15} /> {l} connection
                </button>
              ))}
            </div>
            <div className="p-5">
              {tab === 'mcp' && (
                <div className="mb-5">
                  <div className="mb-2 text-xs font-semibold text-cs-text-secondary">
                    Which agent are you connecting?
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {MCP_CLIENTS.map((c) => (
                      <button key={c.id}
                        type="button"
                        data-testid={`mcp-client-${c.id}`}
                        onClick={() => setMcpClient(c.id)}
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                          mcpClient === c.id
                            ? 'border-cs-primary-500 bg-cs-primary-500/10 text-cs-primary-400'
                            : 'border-cs-border text-cs-text-secondary hover:bg-cs-surface-hover',
                        )}
                      >
                        <Icon name={c.icon} size={13} /> {c.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 gap-6 md:grid-cols-[300px_1fr]">
                {/* steps */}
                <div>
                  <div className="mb-4 text-[13.5px] leading-relaxed text-cs-text-secondary" data-testid="connect-intro">
                    {tab === 'mcp'
                      ? mcpC.intro
                      : tab === 'api'
                        ? 'Call the Context API directly from any backend — no SDK required.'
                        : 'Use the official Python client for the smoothest integration.'}
                  </div>
                  <div className="flex flex-col gap-3.5">
                    {(tab === 'mcp' ? mcpC.steps : steps[tab]).map((s, i) => (
                      <div key={i} className="flex gap-3">
                        <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-cs-primary-500/15 text-xs font-bold text-cs-primary-400">
                          {i + 1}
                        </div>
                        <div className="pt-0.5 text-[13px] leading-snug text-cs-text-primary">{s}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* right pane */}
                <div className="min-w-0">
                  {tab === 'mcp' &&
                    (mcpC.type === 'hosted' ? (
                      <div>
                        <div className="rounded-xl border border-cs-border bg-cs-surface-page px-4 py-2">
                          <CredRow label="Server URL" value={projectMcpUrl} copy={projectMcpUrl} wide />
                          <CredRow label="Authorization" value={`Bearer ${keyShort}`} copy={`Bearer ${keyForSnippet}`} wide />
                        </div>
                        {mcpClient === 'other' && (
                          <div className="mt-3">
                            <div className="mb-2 text-[11.5px] text-cs-text-muted">
                              Client uses a config file instead? Use this:
                            </div>
                            <CodeBlock title="mcp.json" lang="json" code={mcpCode} />
                          </div>
                        )}
                      </div>
                    ) : (
                      <CodeBlock title={mcpC.title ?? 'config.json'} lang="json" code={mcpCode} />
                    ))}
                  {tab === 'api' && <CodeBlock title="Terminal" lang="bash" code={apiCode} />}
                  {tab === 'sdk' && (
                    <CodeBlock title="agent.py" lang="python" code={pyCode} />
                  )}
                  <div className="mt-3 flex items-center gap-2 text-xs text-cs-text-muted">
                    <Icon name="checkcircle" size={14} className="text-cs-success-400" />
                    {tab === 'mcp'
                      ? 'Once connected, your agent queries live, self-organizing context — no syncing.'
                      : 'Same self-organizing context, served three ways. Pick whichever fits your stack.'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* closing reassurance */}
          <div className="mt-11 rounded-2xl border border-cs-border bg-gradient-to-br from-cs-primary-500/10 to-transparent px-6 py-7 text-center">
            <div className="mb-1.5 text-lg font-semibold tracking-tight">
              No schema to define. No pipelines to maintain.
            </div>
            <div className="mx-auto max-w-[560px] text-sm leading-relaxed text-cs-text-secondary">
              Wexa keeps your context organized, current and queryable around the clock — so your
              agents always reason over the freshest picture of your business.
            </div>
          </div>
        </div>
      </div>

      {wsOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setWsOpen(false)} aria-hidden />
      )}
    </div>
  );
}

function CredRow({
  label,
  value,
  copy,
  testid,
  wide,
}: {
  label: string;
  value: string;
  copy: string;
  testid?: string;
  wide?: boolean;
}) {
  return (
    <div className="flex items-center gap-3 border-b border-cs-border py-2 last:border-b-0">
      <span className={cn('flex-shrink-0 text-xs text-cs-text-muted', wide ? 'w-36' : 'w-32')}>
        {label}
      </span>
      <span data-testid={testid} className="flex-1 truncate font-mono text-[12.5px] text-cs-text-primary">
        {value}
      </span>
      <CopyBtn text={copy} />
    </div>
  );
}
